import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { fetchFreshSeq } from '@/lib/crm/core/utils';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { logger } from '@/lib/logger';

import type { PipelineCardConfig } from '../utils/cardConfig';
import { buildDefaultCardConfig } from '../utils/cardConfigDefaults';
import {
  extractConfigNames,
  validateCardConfig,
} from '../utils/cardConfigResolution';

export interface UsePipelineCardConfigResult {
  config: PipelineCardConfig;
  propertyNames: string[];
  associationNames: string[];
  updateConfig: (next: PipelineCardConfig) => void;
  resetConfig: () => void;
}

/**
 * Manages pipeline card field layout configuration.
 *
 * When `pipelineEntityId` and `pipelineEntityTypeName` are provided, the config
 * is persisted on the pipeline CRM entity via the `ahoy_card_config` JSON
 * property (read from `serverCardConfig`, written via submit_events). When they
 * are absent (e.g., create-pipeline flow where no entity exists yet), the hook
 * falls back to localStorage keyed by `pipelineCardConfig:{orgId}:{entityType}`.
 */
export function usePipelineCardConfig(
  orgId: number | undefined,
  entityType: string,
  propertySchemas: Record<string, PropertyTypeSchema>,
  associationTypeMap: Map<string, string>,
  pipelineEntityId?: string | null,
  pipelineEntityTypeName?: string,
  serverCardConfig?: unknown
): UsePipelineCardConfigResult {
  const queryClient = useQueryClient();
  const { user } = useAuthInfo();

  // Entity-backed mode: config comes from the pipeline's ahoy_card_config
  // property. localStorage mode: config comes from browser storage.
  const useEntityMode = !!(pipelineEntityId && pipelineEntityTypeName);

  const storageKey =
    !useEntityMode && orgId
      ? `pipelineCardConfig:${orgId}:${entityType}`
      : null;

  // Version counter forces re-derivation of the config memo after a
  // localStorage write (entity mode doesn't need this — it re-derives from
  // serverCardConfig, which the parent updates via query invalidation).
  const [localVersion, setLocalVersion] = useState(0);

  const config = useMemo(() => {
    const fallback = buildDefaultCardConfig(
      entityType,
      propertySchemas,
      associationTypeMap
    );

    // --- Entity-backed mode ---
    if (useEntityMode) {
      if (
        serverCardConfig == null ||
        typeof serverCardConfig !== 'object' ||
        !Array.isArray((serverCardConfig as PipelineCardConfig).rows)
      ) {
        return fallback;
      }

      const parsed = serverCardConfig as PipelineCardConfig;
      if (parsed.rows.length === 0) {
        return { rows: [] };
      }

      const validated = validateCardConfig(
        parsed,
        propertySchemas,
        associationTypeMap
      );
      return validated.rows.length === 0 ? fallback : validated;
    }

    // --- localStorage fallback ---
    if (!storageKey) {
      return fallback;
    }

    try {
      void localVersion; // force re-read after each persist/reset
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return fallback;
      }

      const parsed = JSON.parse(raw) as PipelineCardConfig;
      if (!parsed || !Array.isArray(parsed.rows)) {
        return fallback;
      }

      if (parsed.rows.length === 0) {
        return { rows: [] };
      }

      const validated = validateCardConfig(
        parsed,
        propertySchemas,
        associationTypeMap
      );
      if (validated.rows.length === 0) {
        return fallback;
      }

      return validated;
    } catch {
      return fallback;
    }
  }, [
    useEntityMode,
    serverCardConfig,
    storageKey,
    entityType,
    propertySchemas,
    associationTypeMap,
    localVersion,
  ]);

  const { propertyNames, associationNames } = useMemo(
    () => extractConfigNames(config),
    [config]
  );

  const updateConfig = useCallback(
    (next: PipelineCardConfig) => {
      // --- Entity-backed mode: submit property update event ---
      if (
        useEntityMode &&
        orgId &&
        pipelineEntityId &&
        pipelineEntityTypeName
      ) {
        const userId = user?.id;
        if (!userId) {
          logger.warn('Cannot save card config: user not available');
          return;
        }

        // Fire-and-forget: the events API call runs in the background.
        // The caller (handleSaveCardConfig) already shows a toast and
        // resets the draft optimistically — the query invalidation below
        // ensures the server data catches up on the next fetch cycle.
        void (async () => {
          try {
            const seq = await fetchFreshSeq(
              orgId,
              pipelineEntityTypeName,
              pipelineEntityId
            );
            await submitEventsOptimistic({
              orgId,
              userId,
              entityTypeName: pipelineEntityTypeName as EntityTypeName,
              primaryEntityId: pipelineEntityId,
              fallbackSeq: seq,
              errorToast: false,
              build: b =>
                b.propertiesUpdated(pipelineEntityId, pipelineEntityTypeName, {
                  ahoy_card_config: next,
                }),
            });

            await queryClient.invalidateQueries({
              queryKey: ['pipelines', orgId],
              exact: false,
            });
          } catch (err) {
            logger.error(
              'Failed to save pipeline card config',
              err instanceof Error ? err : new Error(String(err)),
              {
                org_id: orgId,
                entity_id: pipelineEntityId,
              }
            );
          }
        })();

        return;
      }

      // --- localStorage fallback ---
      if (!storageKey) {
        return;
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
        setLocalVersion(v => v + 1);
      } catch (err) {
        console.warn('Failed to persist pipeline card config', err);
      }
    },
    [
      useEntityMode,
      orgId,
      pipelineEntityId,
      pipelineEntityTypeName,
      user,
      queryClient,
      storageKey,
    ]
  );

  const resetConfig = useCallback(() => {
    if (useEntityMode && orgId && pipelineEntityId && pipelineEntityTypeName) {
      const userId = user?.id;
      if (!userId) {
        return;
      }

      // Clear the card config by setting it to null on the entity.
      void (async () => {
        try {
          const seq = await fetchFreshSeq(
            orgId,
            pipelineEntityTypeName,
            pipelineEntityId
          );
          await submitEventsOptimistic({
            orgId,
            userId,
            entityTypeName: pipelineEntityTypeName as EntityTypeName,
            primaryEntityId: pipelineEntityId,
            fallbackSeq: seq,
            errorToast: false,
            build: b =>
              b.propertiesUpdated(pipelineEntityId, pipelineEntityTypeName, {
                ahoy_card_config: null,
              }),
          });

          await queryClient.invalidateQueries({
            queryKey: ['pipelines', orgId],
            exact: false,
          });
        } catch (err) {
          logger.error(
            'Failed to reset pipeline card config',
            err instanceof Error ? err : new Error(String(err)),
            {
              org_id: orgId,
              entity_id: pipelineEntityId,
            }
          );
        }
      })();

      return;
    }

    // localStorage fallback
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
        setLocalVersion(v => v + 1);
      } catch (err) {
        console.warn('Failed to reset pipeline card config', err);
      }
    } else {
      setLocalVersion(v => v + 1);
    }
  }, [
    useEntityMode,
    orgId,
    pipelineEntityId,
    pipelineEntityTypeName,
    user,
    queryClient,
    storageKey,
  ]);

  return useMemo(
    () => ({
      config,
      propertyNames,
      associationNames,
      updateConfig,
      resetConfig,
    }),
    [config, propertyNames, associationNames, updateConfig, resetConfig]
  );
}
